import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AnyObject, ObjectSchema } from 'yup';

import { FormStepId } from 'shared/config/formStepId';

import type { TCreditFormCommonFields, TCreditFormState } from 'entities/creditForm';
import {
  setIsStepsFullFilled,
  WorkplaceFormValidationSchema,
  CreditPurposeFormValidationSchema,
  ContactsFormValidationSchema,
  PassportFormValidationSchema,
  AdditionalInfoFormValidationSchema,
  selectAllFormFields,
} from 'entities/creditForm';
import type { TUserState } from 'entities/user';
import { accountSelector } from 'entities/user';

export type TStepsValidateErrors = Array<{
  formStepId: ValuesOf<typeof FormStepId>;
  errorFields: Array<keyof TCreditFormCommonFields>;
}>;

type StepFlags = {
  isCreditPurposeFullFilled: boolean;
  isContactsFullFilled: boolean;
  isPassportFullFilled: boolean;
  isWorkplaceFullFilled: boolean;
  isAdditionalDataFullFilled: boolean;
};

const validateStep = async (
  validationSchema: ObjectSchema<AnyObject>,
  formFields: Partial<TCreditFormCommonFields>,
  formStepId: ValuesOf<typeof FormStepId>,
  errorSteps: TStepsValidateErrors,
) => {
  try {
    await validationSchema.validate(formFields, { abortEarly: true });
  } catch (err) {
    if (err.path) {
      errorSteps.push({
        formStepId,
        errorFields: [err.path],
      });
    }
    return false;
  }
  return true;
};

export const validateUserDataThunk = createAsyncThunk<
  void,
  {
    onComplete?: () => void;
    onSuccess?: () => void;
    onError?: (stepsValidateErrors: TStepsValidateErrors) => void;
    stepsToValidate?: Array<ValuesOf<typeof FormStepId>>;
  },
  { state: TCreditFormState & TUserState }
>(
  'validateUserData/validateUserDataThunk',
  async ({ onComplete, onSuccess, onError, stepsToValidate }, { dispatch, getState }) => {
    const user = accountSelector(getState());

    const steps = !user
      ? [FormStepId.CreditPurposeInfo]
      : stepsToValidate || [
          FormStepId.CreditPurposeInfo,
          FormStepId.ContactsInfo,
          FormStepId.PassportInfo,
          FormStepId.WorkplaceInfo,
          FormStepId.AdditionalInfo,
        ];

    const formFields = selectAllFormFields(getState());

    const errorSteps: TStepsValidateErrors = [];

    const flags: StepFlags = {
      isCreditPurposeFullFilled: false,
      isContactsFullFilled: false,
      isPassportFullFilled: false,
      isWorkplaceFullFilled: false,
      isAdditionalDataFullFilled: false,
    };

    const schemaByStep: Record<ValuesOf<typeof FormStepId>, () => ObjectSchema<AnyObject>> = {
      [FormStepId.CreditPurposeInfo]: () => CreditPurposeFormValidationSchema(),
      [FormStepId.ContactsInfo]: () => ContactsFormValidationSchema(true),
      [FormStepId.PassportInfo]: () => PassportFormValidationSchema(),
      [FormStepId.WorkplaceInfo]: () => WorkplaceFormValidationSchema(),
      [FormStepId.AdditionalInfo]: () => AdditionalInfoFormValidationSchema(),
    };

    const flagKeyByStep: Record<ValuesOf<typeof FormStepId>, keyof StepFlags> = {
      [FormStepId.CreditPurposeInfo]: 'isCreditPurposeFullFilled',
      [FormStepId.ContactsInfo]: 'isContactsFullFilled',
      [FormStepId.PassportInfo]: 'isPassportFullFilled',
      [FormStepId.WorkplaceInfo]: 'isWorkplaceFullFilled',
      [FormStepId.AdditionalInfo]: 'isAdditionalDataFullFilled',
    };

    await Promise.all(
      steps.map((stepId) =>
        validateStep(schemaByStep[stepId](), formFields, stepId, errorSteps).then(
          (result) => (flags[flagKeyByStep[stepId]] = result),
        ),
      ),
    );

    const filteredFlags = steps.reduce<Partial<StepFlags>>((acc, stepId) => {
      const flagKey = flagKeyByStep[stepId];
      acc[flagKey] = flags[flagKey];
      return acc;
    }, {});

    dispatch(setIsStepsFullFilled(filteredFlags));

    const isSuccess = steps.every((stepId) => flags[flagKeyByStep[stepId]]);

    if (isSuccess) {
      onSuccess?.();
    } else {
      onError?.(errorSteps);
    }
    onComplete?.();
  },
);
